import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ColumnDef, Row, SortingState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DataDisplayTable } from 'ui-kit';
import { FilterValue, PillFilters } from 'features/common-elements';

import { useQuery } from '@apollo/client';

import { COMPANY_SALES_QUERY, OPEN_SALES_QUERY } from 'query/sales';
import { CompanySales, OpenSales } from 'query/__generated__/sales';

import { salesFiltersConfig, SalesFiltersConfig } from './salesFilters.config';
import { getNextRound } from './salesUtils';
import {
  BIDDER_STATUS_LABEL,
  BIDDER_STATUS_PILL,
  saleStatusBadge,
  SECONDARY_BUTTON_LIGHT_THEME,
} from './styles';

type OpenSale = OpenSales['openSales'][number];
// DataDisplayTable's row type is generalized from pool/listing/deal data and requires
// last_updated_date; SaleAnnouncement (the sales-index shape) has no such field, so this is
// an unused placeholder to satisfy the shared component's contract.
type SaleRow = OpenSale & { last_updated_date: string };

type SaleOwnership = 'own' | 'other' | 'unknown';

const formatDate = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('M/d/yyyy') : '-';
};

const SalesIndexSkeleton: FC = () => (
  <div
    className="mt-3 h-full rounded-lg bg-background-surface p-3 light:bg-white"
    style={{ gridArea: 'page' }}
  >
    <h1 className="mb-4 font-heebo text-lg font-medium text-white light:text-gray-900">
      Sales Index
    </h1>
    <div className="space-y-2">
      {[...Array(3)].map((_, index) => (
        <div
          key={index}
          className="h-14 animate-pulse rounded border border-slate-500 border-transparent-0 bg-background-canvas light:border-gray-200 light:bg-gray-100"
        />
      ))}
    </div>
  </div>
);

export const RequestAccessCell: FC<{ sale: OpenSale; ownership: SaleOwnership }> = ({
  sale,
  ownership,
}) => {
  const navigate = useNavigate();

  // openSales lists the seller's own OPEN sale like anyone else's (bidder_status null), and
  // loan-service refuses a seller's requestSaleAccess, so the seller gets a link to it instead.
  if (ownership === 'own') {
    return (
      <Link
        to={`/sales/${sale.sale_id}`}
        data-testid="sale-row-own-sale"
        // The row's own click handler navigates too; letting both fire pushes the sale page twice.
        onClick={(event) => event.stopPropagation()}
        className="inline-block rounded bg-slate-700 px-2 py-1 text-center text-xs font-bold text-white hover:underline"
      >
        Your sale
      </Link>
    );
  }

  if (ownership === 'unknown') {
    return <span className="animate-pulse text-gray-500 light:text-gray-700">…</span>;
  }

  if (sale.bidder_status) {
    const pillClass = BIDDER_STATUS_PILL[sale.bidder_status] ?? 'bg-slate-700 text-white';
    return (
      <div
        data-testid="sale-row-bidder-status"
        className={`inline-block rounded px-2 py-1 text-center text-xs font-bold ${pillClass}`}
      >
        {BIDDER_STATUS_LABEL[sale.bidder_status] ?? sale.bidder_status}
      </div>
    );
  }

  // loan-service refuses requestSaleAccess once the cut-off passes (sale_bidder.go), so a button
  // here would lead to a request that can only fail.
  if (new Date(sale.access_cutoff_at).getTime() <= Date.now()) {
    return (
      <span
        data-testid="sale-row-access-closed"
        className="text-xs text-gray-400 light:text-gray-700"
      >
        Access closed on {formatDate(sale.access_cutoff_at)}
      </span>
    );
  }

  // The CA and the PEC summary come before a request (Exhibit 6), and they are on the sale page.
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // The row's own click handler navigates too; letting both fire pushes the sale page twice.
    event.stopPropagation();
    navigate(`/sales/${sale.sale_id}`);
  };

  return (
    <Button
      size="small"
      type="secondary"
      label="Request Access"
      data-testid="sale-request-access"
      className={SECONDARY_BUTTON_LIGHT_THEME}
      onClick={handleClick}
    >
      Request Access
    </Button>
  );
};

const saleColumns: ColumnDef<SaleRow, any>[] = [
  {
    accessorKey: 'sale_name',
    header: 'Sale Name',
    // The column is pinned: sized to a long name, it covers the columns beside it once the table
    // scrolls sideways.
    cell: (props) => (
      <span
        data-testid="sale-row-name"
        title={props.row.original.sale_name}
        className="block max-w-44 truncate font-medium text-purple-300 light:text-purple-600"
      >
        {props.row.original.sale_name}
      </span>
    ),
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: (props) => {
      const { label, pillClass } = saleStatusBadge(props.row.original);
      return (
        <span
          data-testid="sale-row-status"
          className={`inline-block rounded px-2 py-1 text-center text-xs font-bold ${pillClass}`}
        >
          {label}
        </span>
      );
    },
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'sale_keys',
    header: 'Sale Keys',
    // One key per line keeps the column narrow: several keys on one line widen the table past the
    // screen, and it then scrolls under the pinned name column.
    cell: (props) => {
      const keys = props.row.original.sale_keys;
      if (keys.length === 0) return <span data-testid="sale-row-keys">-</span>;
      return (
        <ul data-testid="sale-row-keys">
          {keys.map((key) => (
            <li key={key}>{key}</li>
          ))}
        </ul>
      );
    },
    meta: { numeric: false, alignment: 'left' },
  },
  {
    id: 'bid_deadline',
    accessorFn: (sale) => getNextRound(sale.rounds)?.deadline_at ?? '',
    header: 'Next Bid Deadline',
    cell: (props) => {
      const round = getNextRound(props.row.original.rounds);
      return (
        <span data-testid="sale-row-deadline">{round ? formatDate(round.deadline_at) : '-'}</span>
      );
    },
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'access_cutoff_at',
    header: 'Access Cutoff',
    cell: (props) => (
      <span data-testid="sale-row-cutoff">{formatDate(props.row.original.access_cutoff_at)}</span>
    ),
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'offering_count',
    header: 'Pools',
    cell: (props) => {
      const count = props.row.original.offering_count;
      return (
        <span data-testid="sale-row-pools">
          {count} pool{count === 1 ? '' : 's'}
        </span>
      );
    },
    meta: { numeric: true, alignment: 'left' },
  },
];

const getSaleRowProps = (row: Row<SaleRow>) =>
  ({
    'data-testid': 'sale-row',
    'data-sale-id': row.original.sale_id,
    className: 'cursor-pointer',
  }) as React.HTMLAttributes<HTMLTableRowElement>;

export const SalesIndex: FC = () => {
  const navigate = useNavigate();
  const { data, loading, error, stopPolling } = useQuery<OpenSales>(OPEN_SALES_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<FilterValue<SalesFiltersConfig>[]>([]);

  // Same poll as openSales: a sale the seller's company opens after this page loads would
  // otherwise show up in openSales with a Request Access button until a reload.
  const {
    data: companyData,
    loading: companyLoading,
    stopPolling: stopCompanyPolling,
  } = useQuery<CompanySales>(COMPANY_SALES_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });

  useEffect(() => {
    return () => {
      stopPolling();
      stopCompanyPolling();
    };
  }, [stopPolling, stopCompanyPolling]);

  // companySales also returns the sales the caller is bidding in (as SaleAnnouncement); only a
  // Sale is the caller's own. A failed companySales falls back to 'other' (the plain Request
  // Access flow) rather than blocking the index: loan-service still refuses the seller's request.
  const ownSaleIds = useMemo(
    () =>
      new Set(
        (companyData?.companySales ?? [])
          .filter((sale) => sale.__typename === 'Sale')
          .map((sale) => sale.sale_id),
      ),
    [companyData],
  );
  const ownershipUnknown = companyLoading && !companyData;
  const ownershipOf = useCallback(
    (saleId: string): SaleOwnership => {
      if (ownershipUnknown) return 'unknown';
      return ownSaleIds.has(saleId) ? 'own' : 'other';
    },
    [ownershipUnknown, ownSaleIds],
  );

  const columns = useMemo<ColumnDef<SaleRow, any>[]>(
    () => [
      ...saleColumns,
      {
        id: 'access',
        header: 'Access',
        cell: (props) => (
          <RequestAccessCell
            sale={props.row.original}
            ownership={ownershipOf(props.row.original.sale_id)}
          />
        ),
      },
    ],
    [ownershipOf],
  );

  // Polls every 5s; a single transient failure shouldn't blow away the table, the user's filters,
  // and their sort state when the last good page of data is still in hand.
  if (error && !data) {
    throw error;
  }

  const allSales: SaleRow[] = (data?.openSales ?? []).map((sale) => ({
    ...sale,
    last_updated_date: '',
  }));

  // No server-side filter support for openSales; the sales list fits comfortably in memory,
  // so filters just narrow the already-fetched array, same as RemittanceUploadHistory does.
  const sales = useMemo(() => {
    if (filters.length === 0) return allSales;
    return allSales.filter((sale) => {
      for (const filter of filters) {
        if (filter.name === 'sale_name' && 'values' in filter) {
          const queries = filter.values;
          if (
            queries.length > 0 &&
            !queries.some((q) => sale.sale_name.toLowerCase().includes(q.toLowerCase()))
          ) {
            return false;
          }
        }
      }
      return true;
    });
  }, [allSales, filters]);

  if (loading && !data) {
    return <SalesIndexSkeleton />;
  }

  return (
    <div
      className="mt-3 h-full overflow-auto rounded-lg bg-background-surface p-3 light:bg-white"
      style={{ gridArea: 'page' }}
    >
      <h1 className="mb-4 font-heebo text-lg font-medium text-white light:text-gray-900">
        Sales Index
      </h1>
      <div className="mb-3">
        <PillFilters
          className="light:text-gray-700"
          filtersConfig={salesFiltersConfig}
          currentFilters={filters}
          setCurrentFilters={setFilters}
        />
      </div>
      <DataDisplayTable
        data={sales}
        columns={columns}
        noDataMessage={
          filters.length > 0 ? 'No sales match the current filters.' : 'No open sales right now.'
        }
        getRowId={(sale) => sale.sale_id}
        getRowProps={getSaleRowProps}
        onRowClick={(row) => navigate(`/sales/${row.original.sale_id}`)}
        stylingOptions={{ hoverRowColor: 'gray', supportsLightTheme: true }}
        sorting={{ state: sorting, onSortingChanged: setSorting }}
        columnPinningLeft={['sale_name']}
      />
    </div>
  );
};

export default SalesIndex;
